import * as React from 'react'
import { Award, Loader2 } from 'lucide-react'
import { sb } from '@/lib/supabase'

/**
 * The public wall: who finished a program, for the people who chose to say so.
 *
 * The URL stays /certified and the heading stays "Certified". The rest of the
 * portal now says Programs, but this page is the one people are GIVEN — a link
 * in an email signature or a LinkedIn profile — so changing either would break
 * what someone already shared, and "Certified" is the claim a reader outside
 * DPL understands. The body copy says program, which is the thing they
 * finished.
 *
 * Runs signed out, like /status, and for the same reason it must be read with
 * the same suspicion: this is the only unauthenticated read in the schema. It
 * calls ONE function — `public.certification_wall()` — which takes no arguments
 * and returns a display name, the certification, and the date. `anon` still has
 * no grant on any table, so there is nothing here to widen by accident and
 * nothing to probe: a caller cannot ask about a person, only read the list.
 *
 * Everyone on it opted in (`public_visible`, default false) and was approved by
 * a DPL coach (0039). Both are required by the function's own WHERE clause, not
 * by this page.
 */
interface WallRow {
  holder_id: string
  person: string
  certification: string
  certification_slug: string
  approved_on: string | null
}

export function CertificationWallPage() {
  const [rows, setRows] = React.useState<WallRow[] | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    void (async () => {
      const { data, error: err } = await sb().rpc('certification_wall')
      if (err) setError(err.message)
      else setRows((data ?? []) as WallRow[])
    })()
  }, [])

  // Grouped by certification, because the question people arrive with is "who
  // holds THIS one", not "what happened most recently".
  const groups = React.useMemo(() => {
    const byCert = new Map<string, { title: string; people: WallRow[] }>()
    for (const r of rows ?? []) {
      const g = byCert.get(r.certification_slug) ?? { title: r.certification, people: [] }
      g.people.push(r)
      byCert.set(r.certification_slug, g)
    }
    return [...byCert.values()]
  }, [rows])

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-3xl">
        <header className="mb-8 flex items-center gap-3">
          <img
            src="https://dontpaniclabs.com/wp-content/uploads/2023/06/DPL-full-color-2-o.svg"
            alt="Don't Panic Labs"
            className="h-9"
          />
          <div className="min-w-0">
            <h1 className="text-lg font-semibold leading-tight">Certified</h1>
            <p className="text-xs text-muted-foreground">
              People who finished a Don’t Panic Labs program and chose to be listed.
            </p>
          </div>
        </header>

        {error && (
          <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
            {error}
          </p>
        )}

        {!rows && !error && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {rows && rows.length === 0 && (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <Award className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
            <p className="text-sm font-medium">Nobody is listed yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              A program appears here once a DPL coach approves it and the person chooses to
              show it.
            </p>
          </div>
        )}

        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.title}>
              <h2 className="mb-2.5 flex items-baseline gap-2 text-sm font-semibold">
                <Award className="h-4 w-4 shrink-0 text-primary" />
                {g.title}
                <span className="text-xs font-normal text-muted-foreground">
                  {g.people.length} {g.people.length === 1 ? 'person' : 'people'}
                </span>
              </h2>
              <ul className="divide-y rounded-lg border bg-card">
                {g.people.map((p) => (
                  <li key={p.holder_id} className="flex items-center gap-3 px-4 py-2.5">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{p.person}</span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {p.approved_on
                        ? new Date(p.approved_on).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                          })
                        : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <p className="mt-8 text-xs text-muted-foreground">
          Listing is the holder’s choice and is off by default. Nothing else about a person — their
          team, their email, their scores — is published here or readable without signing in.
        </p>
      </div>
    </div>
  )
}
